// src/components/forms/shared/RadioButtonGroup.tsx

import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { colors } from '../../../theme/theme';
import {
  getAccessibilityRadioHover,
  RadioAccessibilityHoverProps,
} from './AccessibilityHoverStyles';
import { useAccessibility } from '../../../contexts/AccessibilityContext';
import EventToast from '../../common/EventToast';

interface RadioOption {
  value: string;
  label: string;
}

interface RadioButtonGroupProps extends RadioAccessibilityHoverProps {
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  direction?: 'horizontal' | 'vertical';
  directionForMobile?: 'horizontal' | 'vertical';
  name?: string;
  required?: boolean;
  // Forwarded to the first option's label so parents can focus the group.
  inputRef?: React.Ref<HTMLLabelElement>;
  // Fires for keys not consumed internally (Space selects). Lets parents
  // implement keyboard navigation (e.g. Enter-as-Tab) across the form.
  onKeyDown?: (e: React.KeyboardEvent<HTMLLabelElement>) => void;
}

const RadioContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'directionForMobile',
})<{
  direction: 'horizontal' | 'vertical';
  directionForMobile?: 'horizontal' | 'vertical';
}>(({ theme, direction, directionForMobile }) => {
  // Determine mobile direction: use directionForMobile if provided, otherwise use original direction
  const mobileDirection = directionForMobile ?? direction;

  return {
    display: 'flex',
    flexDirection: direction === 'horizontal' ? 'row' : 'column',
    gap: direction === 'horizontal' ? '2rem' : '1rem',
    alignItems: direction === 'horizontal' ? 'center' : 'flex-start',
    padding: '0 0 0 1rem',

    // Mobile: use directionForMobile prop if provided, otherwise keep original direction
    [theme.breakpoints.down('sm')]: {
      flexDirection: mobileDirection === 'horizontal' ? 'row' : 'column',
      gap: mobileDirection === 'horizontal' ? '2rem' : '1rem',
      alignItems: mobileDirection === 'horizontal' ? 'center' : 'flex-start',
    },
  };
});

const RadioLabel = styled('label', {
  shouldForwardProp: (prop) =>
    prop !== 'disabled' &&
    prop !== 'checked' &&
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverBorderRadius',
    ].includes(prop as string),
})<
  { disabled?: boolean; checked?: boolean } & RadioAccessibilityHoverProps & {
      accessibilityMode?: boolean;
    }
>(({ theme, disabled, checked, ...accessibilityProps }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '2rem',
  fontWeight: 600,
  color: disabled
    ? theme.palette.text.disabled
    : checked
      ? theme.palette.mode === 'dark'
        ? colors.grey[100]
        : colors.text.dark
      : theme.palette.mode === 'dark'
        ? colors.grey[100]
        : colors.text.dark,
  opacity: disabled ? 0.6 : 1,
  transition: 'color 0.2s ease',
  userSelect: 'none',
  padding: '0 8px 0 5px',

  '&:hover': {
    color: disabled
      ? theme.palette.text.disabled
      : theme.palette.mode === 'dark'
        ? colors.grey[100]
        : colors.text.dark,
    ...(disabled
      ? {}
      : getAccessibilityRadioHover(
          theme,
          Boolean(accessibilityProps.accessibilityMode),
          accessibilityProps
        )),
  },
}));

const HiddenRadio = styled('input')({
  position: 'absolute',
  opacity: 0,
  width: 0,
  height: 0,
});

const CustomRadioButton = styled('span')<{
  checked?: boolean;
  error?: boolean;
  disabled?: boolean;
}>(({ theme, checked, error, disabled }) => ({
  width: '2rem',
  height: '2rem',
  borderRadius: '50%',
  border: `${error ? '4px' : '3px'} solid ${
    error
      ? theme.palette.error.main
      : checked
        ? colors.primary.main
        : colors.grey[900]
  }`,
  marginRight: '0.75rem',
  position: 'relative',
  transition: 'all 0.2s ease',
  backgroundColor: disabled
    ? theme.palette.action.disabledBackground
    : colors.grey[0],
  flexShrink: 0,

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) scale(0)',
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    backgroundColor: disabled
      ? theme.palette.text.disabled
      : colors.primary.main,
    transition: 'transform 0.2s ease',
    ...(checked && {
      transform: 'translate(-50%, -50%) scale(1)',
    }),
  },

  'label:hover &': {
    borderColor: disabled
      ? colors.primary.main
      : error
        ? theme.palette.error.main
        : colors.primary.main,
  },
}));

const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  value,
  onChange,
  options,
  error = false,
  helperText,
  disabled = false,
  direction = 'horizontal',
  directionForMobile,
  name = 'radio-group',
  required = false,
  inputRef,
  onKeyDown,
  accessibilityHoverDisabled,
  accessibilityHoverBackgroundColor,
  accessibilityHoverColor,
  accessibilityHoverBorderRadius,
}) => {
  const theme = useTheme();
  const { accessibilityMode } = useAccessibility();
  const accessibilityHoverProps = {
    accessibilityHoverDisabled,
    accessibilityHoverBackgroundColor,
    accessibilityHoverColor,
    accessibilityHoverBorderRadius,
  };

  // EventToast state for ring ripple effect
  const [showToast, setShowToast] = useState(false);
  const [toastPosition, setToastPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const handleChange = (optionValue: string, event: React.MouseEvent) => {
    if (!disabled) {
      // Don't do anything if clicking the already selected option
      if (value === optionValue) {
        return;
      }

      // Capture the position of the radio button for the ripple
      const radioButton = event.currentTarget as HTMLElement;
      const customRadio = radioButton.querySelector('span') as HTMLElement;

      if (customRadio) {
        const rect = customRadio.getBoundingClientRect();
        const position = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };

        // Show the ripple effect
        setToastPosition(position);
        setShowToast(true);
      }

      // Call the onChange handler
      onChange(optionValue);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLLabelElement>,
    optionValue: string
  ) => {
    if (disabled) return;

    // Space selects the option. Enter is reserved for form-level navigation
    // and is forwarded via onKeyDown below.
    if (e.key === ' ') {
      e.preventDefault();

      // Don't do anything if selecting the already selected option
      if (value === optionValue) {
        return;
      }

      // For keyboard, capture position from the label
      const label = e.currentTarget;
      const customRadio = label.querySelector('span') as HTMLElement;

      if (customRadio) {
        const rect = customRadio.getBoundingClientRect();
        const position = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };

        setToastPosition(position);
        setShowToast(true);
      }

      onChange(optionValue);
      return;
    }

    // Forward unhandled keys (incl. Enter) to the parent.
    onKeyDown?.(e);
  };

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: '600px' }}>
        <RadioContainer
          direction={direction}
          directionForMobile={directionForMobile}
          role="radiogroup"
          aria-required={required}
          aria-invalid={error}
        >
          {options.map((option, idx) => {
            const isChecked = value === option.value;
            const inputId = `${name}-${option.value}`;

            return (
              <RadioLabel
                key={option.value}
                htmlFor={inputId}
                disabled={disabled}
                checked={isChecked}
                accessibilityMode={accessibilityMode}
                {...accessibilityHoverProps}
                onClick={(e) => handleChange(option.value, e)}
                onKeyDown={(e) => handleKeyDown(e, option.value)}
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                ref={idx === 0 ? inputRef : undefined}
              >
                <HiddenRadio
                  type="radio"
                  id={inputId}
                  name={name}
                  value={option.value}
                  checked={isChecked}
                  onChange={() => {}} // Handled by label onClick
                  disabled={disabled}
                  required={required}
                />
                <CustomRadioButton
                  checked={isChecked}
                  error={error}
                  disabled={disabled}
                  aria-hidden="true"
                />
                {option.label}
              </RadioLabel>
            );
          })}
        </RadioContainer>

        {helperText && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              mt: '0.5rem',
              ml: '0.5rem',
            }}
          >
            {error && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    border: `3px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.main
                    }`,
                    backgroundColor: 'transparent',
                    opacity: 0.8,
                    animation: 'radiate 1s ease-out infinite',
                  },
                  '@keyframes radiate': {
                    '0%': {
                      transform: 'translate(-50%, -50%) scale(0.5)',
                      opacity: 0.8,
                    },
                    '100%': {
                      transform: 'translate(-50%, -50%) scale(2.5)',
                      opacity: 0,
                    },
                  },
                }}
              >
                <ErrorOutlineIcon
                  sx={{
                    fontSize: '2rem',
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.main,
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>
            )}
            <Typography
              sx={{
                fontSize: error ? '2rem' : '1.8rem',
                fontWeight: error ? 700 : 500,
                m: 0,
                color:
                  theme.palette.mode === 'dark'
                    ? error
                      ? theme.palette.error.light
                      : theme.palette.grey[400]
                    : error
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
              }}
            >
              {helperText}
            </Typography>
          </Box>
        )}
      </Box>

      {/* EventToast with ring variant */}
      <EventToast
        show={showToast}
        position={toastPosition}
        variant="ring"
        onClose={() => setShowToast(false)}
      />
    </>
  );
};

export default RadioButtonGroup;
